import { nsDfs } from '@qdabra/forms-shared/xmlNamespaces';
import {
    compose,
    filter,
    isNil,
    map,
    pick,
    reject,
    split,
    trim,
    uniq,
} from 'ramda';
import { buildNamespaceManagerFromMap } from '../../../utility/xmlHelpers';
import { ownerDocument } from '../../../xmlUtility';
import {
    paramDsName,
    paramProperties,
    paramSource,
    paramUser,
} from '../constants/parameterNames';
import { typeDataSource, typeOption } from '../constants/parameterTypes';
import { success } from '../helpers/commandResult';
import { buildUserProfileResponseNode } from '../helpers/getUserProfile.helpers';
import { getParamValue } from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';

const SOURCE_ENVIRONMENT_DEFAULT = 'default';
const SOURCE_SITE_USERS = 'siteusers';
const SOURCE_USER_PROFILES = 'profiles';

const getUserProfileErrorDsError =
    'The data source schema is invalid for use with GetUserProfileByName';

const ERROR_FAILED_GET_USER = 'Failed to retrieve data for the specified user.';

const NO_RESULT_ERROR = `${ERROR_FAILED_GET_USER} No results found.`;

const description =
    'Retrieves user information from SharePoint’s user profile store and uses it to populate a GetUserProfileByName SOAP connection.';

const requiredParameters = [];
const optionalParameters = {
    [paramDsName]: {
        description:
            'GetUserProfileByName data source. Defaults to GetUserProfileByName',
        type: typeDataSource,
    },
    [paramUser]: {
        description:
            'User alias. Defaults to the current user. Required when the current user cannot be determined, e.g. in anonymous mode.',
    },
    [paramProperties]: {
        description: `Semicolon-delimited list of specific user profile properties to query. When using /${paramSource}=${SOURCE_USER_PROFILES}, the app must be installed with extra permissions to query the user profile store in order to use this parameter.`,
    },
    [paramSource]: {
        description: `(Defaults to ${SOURCE_ENVIRONMENT_DEFAULT}) Location from which to retrieve user data. Possible values are ${SOURCE_SITE_USERS} for the Site Users List, ${SOURCE_USER_PROFILES} for the User Profile store, and ${SOURCE_ENVIRONMENT_DEFAULT} for the environment default. ${SOURCE_ENVIRONMENT_DEFAULT} equates to ${SOURCE_SITE_USERS} when using server-side queries and ${SOURCE_USER_PROFILES} when using client-side queries.`,
        type: typeOption,
        options: [
            { name: 'Environment default', value: SOURCE_ENVIRONMENT_DEFAULT },
            { name: 'Site Users List', value: SOURCE_SITE_USERS },
            { name: 'User Profile Store', value: SOURCE_USER_PROFILES },
        ],
    },
};

const PROPERTY_WHITE_LIST = [
    'MobilePhone',
    'IsSiteAdmin',
    'FirstName',
    'LastName',
    'WorkPhone',
    'UserName',
    'WebSite',
    'Office',
    'Modified',
    'Created',
];

// String -> [String]
const parsePropertyNames = compose(filter(Boolean), map(trim), split(';'));

const getNamespaces = () =>
    buildNamespaceManagerFromMap({
        dfs: nsDfs,
    });

function resolveSource(source, shpAccess) {
    const sourceLower = source.toLowerCase();

    switch (sourceLower) {
        case SOURCE_ENVIRONMENT_DEFAULT:
            return shpAccess.type === 'server'
                ? SOURCE_SITE_USERS
                : SOURCE_USER_PROFILES;
        case SOURCE_SITE_USERS:
        case SOURCE_USER_PROFILES:
            return sourceLower;
        default:
            throw new Error(`Invalid /${paramSource} value: '${source}'`);
    }
}

// (DataSource, { String: String }) => Promise
const updateDataSource = async (ds, propertyValues) => {
    const dataFields = ds.selectSingle('/*/dfs:dataFields', {
        namespaces: getNamespaces(),
    });

    if (!dataFields) {
        throw new Error(getUserProfileErrorDsError);
    }

    const document = ownerDocument(ds.getNode());

    await dataFields.setContentAsync(
        buildUserProfileResponseNode(document, propertyValues),
    );
};

// (SharePointAccess, String, [String]) => { String: String }
async function getUserFromProfileAsync(spa, userName, propertyNames) {
    const { properties, extraPropertiesError } = await spa.loadUserAsync(
        userName,
        propertyNames,
    );

    const fullError = extraPropertiesError
        ? `An error occurred while retrieving extra properties. This may be due to insufficient app permissions. Consider using the /${paramSource}=${SOURCE_SITE_USERS} parameter. Error: ${extraPropertiesError}`
        : null;

    return {
        properties,
        error: fullError,
    };
}

async function getSiteUserAsync(spa, userName, propertyNames) {
    const results = await spa.getSiteUserAsync(userName);

    const props = pick([...PROPERTY_WHITE_LIST, ...propertyNames], results);
    const extraProps = {
        PreferredName: results.Title,
        LoginName: results.Name,
        AccountName: results.Name,
        Email: results.EMail,
        Picture: results.Picture && results.Picture.Url,
        Title: results.JobTitle,
        WorkEmail: results.EMail,
    };

    return {
        properties: reject(isNil, {
            ...props,
            ...extraProps,
        }),
    };
}

const performQuery = async (source, spa, userName, propertyNames) => {
    try {
        const queryMethod =
            source === SOURCE_SITE_USERS
                ? getSiteUserAsync
                : getUserFromProfileAsync;

        return await queryMethod(spa, userName, propertyNames);
    } catch (e) {
        console.error(e);

        throw new Error(`${ERROR_FAILED_GET_USER}  Error: ${e.message}`);
    }
};

// (SharePointAccess, String, [String]) -> { properties: { String: String }, error: String }
async function getUserAsync(spa, userName, propertyNames, source) {
    const results = await performQuery(source, spa, userName, propertyNames);

    if (!results) {
        throw new Error(NO_RESULT_ERROR);
    }

    return results;
}

async function executeAsync({
    dataSources,
    environment,
    parameters,
    sharePointAccess,
}) {
    const dsName = getParamValue(
        parameters,
        paramDsName,
        'GetUserProfileByName',
    );
    const userName = getParamValue(
        parameters,
        paramUser,
        environment.getUserName(),
    );
    const extraPropertyNames = getParamValue(parameters, paramProperties, '');
    const source = resolveSource(
        getParamValue(parameters, paramSource, SOURCE_ENVIRONMENT_DEFAULT),
        sharePointAccess,
    );

    if (!userName) {
        throw new Error(`Please specify a /${paramUser} value.`);
    }

    const extraPropertyNameList = parsePropertyNames(extraPropertyNames);

    // For backward compatibility purposes, we query Department and Manager
    // as extra properties
    const allExtraProperties = uniq([
        ...extraPropertyNameList,
        'Department',
        'Manager',
    ]);

    const ds = getDataSource(dataSources, dsName);

    const { properties, error } = await getUserAsync(
        sharePointAccess,
        userName,
        allExtraProperties,
        source,
    );

    await updateDataSource(ds, properties);

    return success('', error);
}

const GetUserProfileByName = {
    description,
    executeAsync,
    requiredParameters,
    optionalParameters,
};

export default GetUserProfileByName;
